import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LinearProgress from "@material-ui/core/LinearProgress";

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: "15rem",
        minHeight: "9.35rem",
        boxSizing: "border-box",
        borderRadius: 12,
        boxShadow: theme.palette.type === "dark" 
            ? "0 4px 20px rgba(0, 0, 0, 0.4)" 
            : "0 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "1rem",
        background: theme.palette.background.paper,
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.12)"
            : "1px solid #ced4da",
    },
    noServersAliveAlertCard: {
        border: "2px solid #e11d48",
    },
    containerIcon: {
        position: "relative",
        width: "1.2rem",
        height: "1.2rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.6rem",
        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
        borderRadius: "5px",
        marginBottom: "0.5rem",
    },
    serverIconBg: {
        background: "#10b981",
    },
    noServersAliveAlertIconBg: {
        background: "#e11d48",
    },
    titleLayout: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        gap: "0.75rem",
        marginBottom: "0.5rem",
    },
    cardTitle: {
        fontSize: "1rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
    },
    tooltip: {
        backgroundColor: "#1C1E20",
        padding: "0.3rem 0.5rem",
        borderRadius: "4px",
        border: "1px solid #6c757d",
        color: "#ffffff",
        fontSize: "12px",
    },
    infoIcon: {
        width: "1.2rem",
        height: "1.2rem",
        color: theme.palette.text.secondary,
    },
    containerCardValue: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
    },
    cardValueText: {
        fontSize: "1.6rem",
        fontWeight: 600,
        color: theme.palette.text.primary,
        transition: "all 0.3s ease",
    },
    progressContainer: {
        width: "100%",
    },
    progressValueText: {
        fontSize: "1.1rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
    },
    progressRoot: {
        height: 8,
        borderRadius: 6,
        backgroundColor: theme.palette.type === "dark" 
            ? "rgba(255, 255, 255, 0.1)" 
            : "#e5e7eb",
    },
    progressTrack: {
        backgroundColor: theme.palette.type === "dark" 
            ? "rgba(255, 255, 255, 0.1)" 
            : "#e5e7eb",
    },
    progressBar: {
        borderRadius: 6,
        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    },
}));
export default function StatCard({ cardData, queueAnalitycsSummary, mmActivitySummary, serversAliveCount }) {
    const classes = useStyles();

    const resolvedValue =
        cardData.id === "online-players" ? (queueAnalitycsSummary?.onlineUserCount ?? 0) :
        cardData.id === "players-in-queue" ? (queueAnalitycsSummary?.totalPlayersInQueue ?? 0) :
        cardData.id === "active-lobbies" ? (queueAnalitycsSummary?.totalLobbies ?? 0) :
        cardData.id === "average-queue-duration" ? (queueAnalitycsSummary?.averageQueueDuration ?? 0) :
        cardData.id === "matches-live" ? (mmActivitySummary?.matchesLive ?? 0) :
        cardData.id === "matches-created-today" ? (mmActivitySummary?.matchesCreatedToday ?? 0) :
        cardData.id === "matches-completed" ? (mmActivitySummary?.matchesCompletedToday ?? 0) :
        cardData.id === "average-match-duration" ? (mmActivitySummary?.averageMatchDuration ?? 0) :
        cardData.id === "completion-rate" ? (mmActivitySummary?.completionRate ?? 0) :
        cardData.id === "held-groups-now" ? (mmActivitySummary?.heldGroupsNow ?? 0) :
        cardData.id === "blocked-players-now" ? (mmActivitySummary?.blockedPlayersNow ?? 0) :
        cardData.id === "blocked-groups-last-tick" ? (mmActivitySummary?.blockedGroupsLastTick ?? 0) :
        cardData.id === "number-of-servers" ? (serversAliveCount ?? 0) :
        0;

    const formatAvgDuration = (seconds) => {
        const totalSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
        const minutes = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        const pad = (n) => (n < 10 ? `0${n}` : String(n));
        return `${pad(minutes)}:${pad(secs)}${minutes > 0 ? "m" : "s"}`;
    };

    return (
        <div className={`${classes.cardRoot} ${cardData.id === "number-of-servers" && serversAliveCount <= 0 ? classes.noServersAliveAlertCard : ""}`}>
            <div className={`${classes.containerIcon} ${cardData.id === "number-of-servers" ? classes.serverIconBg : ""} ${cardData.id === "number-of-servers" && serversAliveCount <= 0 ? classes.noServersAliveAlertIconBg : ""}`}>
                {cardData.icon}
            </div>
            <div className={classes.titleLayout}>
                <Typography className={classes.cardTitle}>{cardData.title}</Typography>
                <Tooltip 
                    title={cardData.tooltipText}
                    classes={{ tooltip: classes.tooltip }}
                >
                    <InfoOutlinedIcon className={classes.infoIcon}/>
                </Tooltip>
            </div>
            <div className={classes.containerCardValue}>
                {cardData.id === "completion-rate" ? (
                    <div className={classes.progressContainer}>
                        <Typography className={classes.progressValueText}>{resolvedValue}%</Typography>
                        <LinearProgress
                            variant="determinate"
                            value={typeof resolvedValue === "number" ? resolvedValue : parseFloat(String(resolvedValue).replace("%", "")) || 0}
                            classes={{
                                root: classes.progressRoot,
                                colorPrimary: classes.progressTrack,
                                barColorPrimary: classes.progressBar,
                            }}
                        />
                    </div>
                ) : cardData.id === "average-match-duration" || cardData.id === "average-queue-duration" ? (
                    <Typography className={classes.cardValueText}>{formatAvgDuration(resolvedValue)}</Typography>
                ) : (
                    <Typography className={classes.cardValueText}>{resolvedValue}</Typography>
                )}
            </div>
        </div>
    );
}